/**
 * DeliberationNew - Nova Deliberação com SSE
 * 
 * Tela para iniciar nova deliberação e acompanhar progresso via SSE.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { streamDeliberation } from '../services/deliberationStreaming';
import type { SSEEvent } from '../services/deliberationStreaming';

export default function DeliberationNew() {
    const navigate = useNavigate();
    const location = useLocation();

    // Pré-preencher com dados do state (re-run)
    const [question, setQuestion] = useState(location.state?.question || '');
    const [context, setContext] = useState(location.state?.context || '');
    const [isRunning, setIsRunning] = useState(false);
    const [events, setEvents] = useState<SSEEvent[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [runId, setRunId] = useState<string | null>(null);

    async function handleStart() {
        if (!question.trim()) {
            setError('Pergunta é obrigatória');
            return;
        }

        setIsRunning(true);
        setEvents([]);
        setError(null);
        setRunId(null);

        const abortController = new AbortController();
        let watchdog: ReturnType<typeof window.setTimeout> | null = null;
        let timedOut = false;

        const resetWatchdog = () => {
            if (watchdog) window.clearTimeout(watchdog);
            watchdog = window.setTimeout(() => {
                timedOut = true;
                abortController.abort(); // não depender de reason
            }, 30000); // 30 segundos sem eventos
        };

        try {
            resetWatchdog();
            for await (const event of streamDeliberation(question, context, abortController.signal)) {
                resetWatchdog(); // Resetar timer a cada evento
                setEvents(prev => [...prev, event]);

                setRunId(prev => prev ?? event.run_id);

                if (event.type === 'error') {
                    if (watchdog) window.clearTimeout(watchdog);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const code = (event.payload as any)?.error || 'ERROR';
                    setError(getErrorMessage(code));
                    break;
                }

                if (event.type === 'done') {
                    if (watchdog) window.clearTimeout(watchdog);
                    setTimeout(() => {
                        navigate(`/deliberations/${event.run_id}`);
                    }, 2000);
                }
            }
        } catch (err) {
            if (watchdog) window.clearTimeout(watchdog);

            // Abort por watchdog (check local flag)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isAbortError = err instanceof DOMException && err.name === 'AbortError';

            if (isAbortError && timedOut) {
                setError('Execução interrompida por inatividade. Você pode verificar o histórico.');
            } else if (err instanceof Error) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const errorObj = err as any;

                if (errorObj.code === 'STREAM_INTERRUPTED' || err.message === 'STREAM_INTERRUPTED') {
                    setError('A conexão foi encerrada, mas a deliberação pode ter continuado. Verifique o histórico.');
                } else {
                    setError(getErrorMessage(err.message, errorObj.remaining));
                }
            } else {
                setError('Erro desconhecido');
            }
        } finally {
            if (watchdog) window.clearTimeout(watchdog);
            setIsRunning(false);
        }
    }

    function getErrorMessage(errorCode: string, remaining?: number): string {
        const remainingText = remaining !== undefined ? ` (Tentativas restantes hoje: ${remaining})` : '';

        const errorMessages: Record<string, string> = {
            'DELIBERATION_IN_PROGRESS': `Você já tem uma deliberação em andamento. Aguarde a conclusão ou acesse o histórico.${remainingText}`,
            'DAILY_LIMIT_REACHED': 'Limite diário de deliberações atingido (10/dia). Tente novamente amanhã.',
            'TOKEN_BUDGET_EXCEEDED': 'Deliberação interrompida por limite de custo. Tente usar o modo rápido.',
            'INVALID_INPUT_SIZE': 'Entrada muito longa. Reduza o tamanho da pergunta ou contexto.',
            'INPUT_SIZE_EXCEEDED': 'Entrada muito longa. Reduza o tamanho da pergunta ou contexto.',
        };

        return errorMessages[errorCode] || errorCode;
    }

    function getErrorAction(error: string | null): 'history' | 'retry' | null {
        if (!error) return null;
        if (error.includes('em andamento')) return 'history';
        if (error.includes('diário')) return 'history';
        if (error.includes('inatividade')) return 'history'; // Timeout handling
        if (error.includes('conexão foi encerrada')) return 'history'; // Stream Interrupted handling
        if (error.includes('custo') || error.includes('longa')) return 'retry';
        return null;
    }

    function getEventIcon(type: string) {
        const icons = {
            stage_started: '▶️',
            stage_completed: '✅',
            error: '❌',
            done: '🎯',
        };
        return icons[type as keyof typeof icons] || '•';
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Nova Deliberação</h1>
                <p className="text-gray-600">
                    Inicie uma deliberação estruturada com 6 etapas de análise
                </p>
            </div>

            {/* Formulário */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pergunta / Problema *
                    </label>
                    <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        disabled={isRunning}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        rows={3}
                        placeholder="Ex: Como melhorar a retenção de usuários no nosso produto?"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contexto (opcional)
                    </label>
                    <textarea
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        disabled={isRunning}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        rows={3}
                        placeholder="Ex: SaaS B2B, churn de 15% ao mês, 500 usuários ativos"
                    />
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 font-medium mb-2">{error}</p>
                        <div className="flex gap-2">
                            {getErrorAction(error) === 'history' && (
                                <button
                                    onClick={() => navigate('/deliberations')}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                >
                                    📋 Ver Histórico
                                </button>
                            )}
                            {getErrorAction(error) === 'retry' && (
                                <button
                                    onClick={() => setError(null)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                >
                                    🔄 Tentar Novamente
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <button
                    onClick={handleStart}
                    disabled={isRunning || !question.trim()}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                >
                    {isRunning ? '⏳ Executando...' : '🚀 Iniciar Deliberação'}
                </button>
            </div>

            {/* Progresso */}
            {events.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                        Progresso ({events.length} eventos)
                    </h2>

                    <div className="space-y-2">
                        {events.map((event, index) => (
                            <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-200"
                            >
                                <span className="text-lg">{getEventIcon(event.type)}</span>
                                <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                        {event.stage || event.type}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {new Date(event.timestamp).toLocaleTimeString('pt-BR')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {isRunning && (
                        <div className="mt-4 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {!isRunning && runId && (
                        <div className="mt-4 text-center">
                            <p className="text-green-600 font-medium mb-2">✅ Deliberação concluída!</p>
                            <button
                                onClick={() => navigate(`/deliberations/${runId}`)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Ver Replay Completo
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
